//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { Alert } from '@components/atoms/alert/Alert';
import Chip from '@components/atoms/chip/Chip';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { useAllLabelsQuery } from '@content-drawer/filters/labels/hooks/useAllLabelsQuery';
import { LabelTypeahead } from '@galleries/components/modals/bulk-add-labels/LabelTypeahead';
import {
  AddLabelStatus,
  calculateHandleAddLabel,
} from '@galleries/components/modals/bulk-add-labels/labelSearchUtils';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import { containsStringSearch, pluralize } from '@utils/stringUtils';

const labelDoesntExistWarning = () => {
  return (
    <Alert type='warning'>
      This label is not present on any items, so it cannot be removed.
    </Alert>
  );
};

interface Props {
  setLabels: (labels: string[]) => void;
  labels: string[];
  isModalOpen: boolean;
}

export const LabelsSearchSection = ({
  setLabels,
  isModalOpen,
  labels,
}: Props) => {
  const isRemoveLabelsModal = useSelector(
    (state) => state.bulkActions.actionLabelsModal.isRemoveLabelsModal,
  );

  // Component state hooks
  const [newLabel, setNewLabel] = useState('');
  const [filteredLabels, setFilteredLabels] = useState<string[]>([]);
  const [showLabelTypeahead, setShowLabelTypeahead] = useState(false);
  const [invalidLabel, setInvalidLabel] = useState(false);
  const [labelDoesntExist, setLabelDoesntExist] = useState(false);

  const [debouncedNewLabel] = useDebounce(newLabel, 250);

  const { data } = useAllLabelsQuery();

  const clientLabels = useMemo(
    () => data?.data.indexObjects.map((obj) => obj.userTag) ?? [],
    [data],
  );

  useEffect(() => {
    // Only display the first 25 labels
    setFilteredLabels(clientLabels.slice(0, 25));
  }, [clientLabels]);

  useEffect(() => {
    // Reset component state on isOpen state change
    setNewLabel('');
    setShowLabelTypeahead(false);
    setInvalidLabel(false);
    setLabelDoesntExist(false);
  }, [isModalOpen]);

  useEffect(() => {
    if (debouncedNewLabel && isDefined(clientLabels)) {
      const labels = containsStringSearch(clientLabels, debouncedNewLabel);
      setFilteredLabels(labels);
      setShowLabelTypeahead(true);
      setInvalidLabel(false);
      setLabelDoesntExist(false);
    }
  }, [debouncedNewLabel, clientLabels]);

  const addLabel = (newLabel: string) => {
    const newLabels = [...labels, newLabel];
    setLabels(newLabels);
  };

  const verifyAndAddLabels = () => {
    // Do a prelim filter of all labels that are already in the list
    const allNewLabels = newLabel
      .split(' ')
      .filter((label) => !labels.includes(label));

    // Dedup labels
    const noDuplicatesNewLabels = new Set(allNewLabels);
    const allNewLabelsUnvalidated = Array.from(noDuplicatesNewLabels);

    // Map over the labels split by a space
    const allNewValidLabels = allNewLabelsUnvalidated.filter((currentLabel) =>
      crl8.shared.tags.valid(currentLabel),
    );

    // Check if there were invalid labels and add all valid labels
    setInvalidLabel(allNewLabels.length !== allNewValidLabels.length);
    setLabels([...labels, ...allNewValidLabels]);
  };

  const emptySearchBox = () => {
    setNewLabel('');
    setShowLabelTypeahead(false);
  };

  const handleAddLabel = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      emptySearchBox();
      const addLabelStatus = calculateHandleAddLabel(
        labels.includes(newLabel),
        isRemoveLabelsModal,
        clientLabels.includes(newLabel),
      );

      // We will either be adding labels to the list in the add labels modal, or the remove labels modal
      // and we could have an invalid label that we will need to show an alert for
      switch (addLabelStatus) {
        case AddLabelStatus.ADD_LABEL: {
          verifyAndAddLabels();
          break;
        }
        case AddLabelStatus.REMOVE_LABEL:
          addLabel(newLabel);
          break;
        case AddLabelStatus.LABEL_DOESNT_EXIST:
          setLabelDoesntExist(true);
          break;
        case AddLabelStatus.NO_ACTION:
          break;
      }
    }
  };

  const handleSearchChange = (labelChange: string) => {
    setNewLabel(labelChange);
    if (labelChange === '') {
      setShowLabelTypeahead(false);
    }
  };

  const handleTypeaheadSelect = (label: string) => {
    if (!labels.includes(label)) {
      addLabel(label);
    }
    emptySearchBox();
  };

  const renderLabel = (currentLabel: string) => {
    const onLabelDelete = () => {
      const newLabels = labels.filter((label) => label !== currentLabel);
      setLabels(newLabels);
    };

    return (
      <StyledChip
        text={currentLabel}
        size='large'
        functionalIcon='close'
        onClick={onLabelDelete}
      />
    );
  };

  return (
    <LabelsAdditionSection>
      <SearchBarContainer>
        <SearchBar
          onKeyUp={handleAddLabel}
          handleClear={emptySearchBox}
          onChange={handleSearchChange}
          value={newLabel}
          spellCheck={false}
          placeholder='Search'
          type='text'
          trackingIdPrefix='label-search'
          visuallyHiddenLabel='Label Search'
        />
        {showLabelTypeahead && (
          <LabelTypeahead
            query={newLabel}
            onSelect={handleTypeaheadSelect}
            labels={filteredLabels}
          />
        )}
      </SearchBarContainer>
      {labelDoesntExist && labelDoesntExistWarning()}
      {invalidLabel && (
        <Alert type='error'>
          Labels can only contain letters, numbers, dashes (-), or underscores
          (_). e.g. flat_lay1
        </Alert>
      )}
      <LabelCount>
        {pluralize(labels.length, 'label')}{' '}
        {isRemoveLabelsModal ? 'removed' : 'added'}
      </LabelCount>
      <LabelsToBeAdded>
        {labels.map((label) => renderLabel(label))}
      </LabelsToBeAdded>
    </LabelsAdditionSection>
  );
};

const SearchBarContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-firection: column;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: ${theme.spacings[3]};
  margin-bottom: ${theme.spacings[3]};
`,
);

const LabelsAdditionSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray1};
  padding-bottom: ${({ theme }) => theme.spacings[4]};
`;

const LabelCount = styled.div`
  ${Mixins.Text.Metatag}
`;

const LabelsToBeAdded = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const StyledChip = styled(Chip)`
  width: fit-content;
  margin-right: ${({ theme }) => theme.spacings[2]};
  margin-top: ${({ theme }) => theme.spacings[2]};
`;
